"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";

const steps = [
  {
    number: "01",
    title: "Start Your Campaign",
    description: "Create your private BabyBank campaign in minutes. Add your story, treatment goals, and target amount. Only you control who sees it.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
      </svg>
    ),
    color: "#2D6A8F",
    bgColor: "#EBF4FA",
    tag: "For Patients",
  },
  {
    number: "02",
    title: "Share Privately",
    description: "Invite only the people you choose — via a private link, email, or text. No public profile, no strangers, no social media exposure.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/>
      </svg>
    ),
    color: "#E8A598",
    bgColor: "#FDF0ED",
    tag: "Invite-Only",
  },
  {
    number: "03",
    title: "Loved Ones Contribute",
    description: "Friends and family donate directly and securely. They can leave private messages of encouragement, choose to give anonymously, or set recurring support.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 21.593c-5.63-5.539-11-10.297-11-14.402 0-3.791 3.068-5.191 5.281-5.191 1.312 0 4.151.501 5.719 4.457 1.59-3.968 4.464-4.447 5.726-4.447 2.54 0 5.274 1.621 5.274 5.181 0 4.069-5.136 8.625-11 14.402z"/>
      </svg>
    ),
    color: "#5A8A6F",
    bgColor: "#D4EAD9",
    tag: "Community Love",
  },
  {
    number: "04",
    title: "Fund Your Treatment",
    description: "Funds go directly to your account or clinic. Track every milestone tied to your treatment plan — from egg retrieval to embryo transfer.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
      </svg>
    ),
    color: "#2D6A8F",
    bgColor: "#EBF4FA",
    tag: "Debt-Free",
  },
];

export default function HowItWorksSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }
    );
    const reveals = sectionRef?.current?.querySelectorAll(".reveal, .reveal-scale");
    reveals?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="how-it-works" className="py-24 md:py-32" ref={sectionRef}
      style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #FDF8F4 100%)" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
          <div className="reveal flex justify-center">
            <span className="section-tag">How It Works</span>
          </div>
          <h2 className="reveal font-heading font-bold text-ink" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", lineHeight: 1.1, letterSpacing: "-0.02em" }}>
            From first step to{" "}
            <span className="text-gradient-primary">funded treatment</span>
          </h2>
          <p className="reveal text-lg text-ink-muted leading-relaxed">
            BabyBank is built around your privacy and peace of mind. Four simple steps to get the support you deserve.
          </p>
        </div>

        {/* Steps grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {steps?.map((step, i) => (
            <div
              key={step?.number}
              className="reveal reveal-scale rounded-3xl p-8 space-y-5 card-hover border border-warm-border-light"
              style={{ background: "#FFFFFF", animationDelay: `${i * 0.1}s` }}
            >
              {/* Step number + icon */}
              <div className="flex items-start justify-between">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ background: step?.bgColor, color: step?.color }}>
                  {step?.icon}
                </div>
                <span className="text-4xl font-heading font-bold" style={{ color: step?.color, opacity: 0.15 }}>
                  {step?.number}
                </span>
              </div>

              {/* Tag */}
              <span className="inline-block px-2.5 py-1 rounded-full text-2xs font-semibold uppercase tracking-wider"
                style={{ background: step?.bgColor, color: step?.color }}>
                {step?.tag}
              </span>

              {/* Content */}
              <div className="space-y-2">
                <h3 className="font-heading font-semibold text-lg text-ink">{step?.title}</h3>
                <p className="text-sm text-ink-muted leading-relaxed">{step?.description}</p>
              </div>

              {/* Connector arrow (hidden on last) */}
              {i < steps?.length - 1 && (
                <div className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 z-10">
                  <div className="w-6 h-6 rounded-full bg-white border-2 border-warm-border flex items-center justify-center">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9AAAB5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="reveal text-center">
          <Link href="/create-campaign" className="btn-primary text-base px-10 py-4">
            Create Your Private Campaign
          </Link>
        </div>
      </div>
    </section>
  );
}